import { createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { appMounted, routePathNameChanged } from "@features/app/model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import type { ITopicWithLessons } from "@features/lessons/model";
import type { ILessonCompletionContract } from "@features/lesson-completion/model";
import type { ILessonCompletionFlowContract, LessonExitStart } from "@features/lesson-completion/ui";
import type { ICurrentLessonContract } from "@features/lessons/ui/current";
import type { ICurrentCourseContract } from "@features/courses/ui/current";
import type { ILessonsContract } from "@features/lessons/model";
import type { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import type { IUserAuthContract } from "@features/student/auth/model";
import type { NextLesson } from "./flow";

export type MiniMapRole = "before" | "completed" | "next";

export type MiniMapNode = {
    lessonId: number;
    /** null when the lesson is not in the loaded course map (the overlay shows a generic label). */
    title: string | null;
    role: MiniMapRole;
    /** Set on a node that starts a different topic than the node before it. */
    topicTitle: string | null;
};

export type LessonTransition = {
    phase: "playing" | "ready";
    nextLesson: NextLesson;
    completedLessonId: number;
    isTopicCompleted: boolean;
    nodes: MiniMapNode[];
    progressFrom: number;
    progressTo: number;
    startedAt: number;
};

/** Timings the overlay enforces: how long the handoff is held, and when a stuck navigation is given up. */
export const TRANSITION_MIN_MS = 2800;
export const TRANSITION_TOPIC_EXTRA_MS = 400;
export const TRANSITION_REDUCED_MOTION_MS = 1200;
export const TRANSITION_TIMEOUT_MS = 6000;
const LESSON_ROUTE = /^\/course\/\d+\/topic\/\d+\/lesson\/\d+/;

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

type FlatLesson = { lessonId: number; title: string; topicTitle: string; topicIndex: number; completed: boolean };

function flatten(courseLessons: ITopicWithLessons[]): FlatLesson[] {
    return courseLessons.flatMap((topic, topicIndex) =>
        topic.lessons.map((lesson) => ({
            lessonId: lesson.id,
            title: lesson.title,
            topicTitle: topic.title,
            topicIndex,
            completed: lesson.enrollmentStatus === UserEnrollmentStatus.Completed
        }))
    );
}

/** Previous, completed and next lesson as the overlay draws them; generic nodes when the map does not know them. */
export function buildMiniMap(
    courseLessons: ITopicWithLessons[],
    completedLessonId: number,
    nextLessonId: number
): MiniMapNode[] {
    const flat = flatten(courseLessons);
    const completedIndex = flat.findIndex((lesson) => lesson.lessonId === completedLessonId);
    const nextIndex = flat.findIndex((lesson) => lesson.lessonId === nextLessonId);
    if (completedIndex === -1 || nextIndex === -1) {
        return [
            { lessonId: completedLessonId, title: null, role: "completed", topicTitle: null },
            { lessonId: nextLessonId, title: null, role: "next", topicTitle: null }
        ];
    }
    const picked: { lesson: FlatLesson; role: MiniMapRole }[] = [];
    if (completedIndex > 0) picked.push({ lesson: flat[completedIndex - 1], role: "before" });
    picked.push({ lesson: flat[completedIndex], role: "completed" });
    picked.push({ lesson: flat[nextIndex], role: "next" });

    return picked.map(({ lesson, role }, index) => {
        const previous = index > 0 ? picked[index - 1].lesson : null;
        const startsTopic = previous !== null && previous.topicIndex !== lesson.topicIndex;
        return {
            lessonId: lesson.lessonId,
            title: lesson.title,
            role,
            topicTitle: startsTopic ? lesson.topicTitle : null
        };
    });
}

/** Course progress after this completion when the backend did not report it: completed lessons + this one. */
export function progressAfterCompletion(courseLessons: ITopicWithLessons[], completedLessonId: number): number | null {
    const flat = flatten(courseLessons);
    if (flat.length === 0) return null;
    const done = new Set(flat.filter((lesson) => lesson.completed).map((lesson) => lesson.lessonId));
    done.add(completedLessonId);
    return clampPercent(Math.round((done.size / flat.length) * 100));
}

const transitionMinimumElapsed = createEvent();
const transitionFinished = createEvent();
const transitionAborted = createEvent();
const transitionStarted = createEvent<LessonTransition>();
const transitionReady = createEvent();

const $transitionEnabled = createStore(false);
const $lessonTransition = createStore<LessonTransition | null>(null)
    .on(transitionStarted, (_, transition) => transition)
    .on(transitionReady, (transition) => (transition ? { ...transition, phase: "ready" as const } : transition));
const $progressBeforeSnapshot = createStore(0);
const $minDurationElapsed = createStore(false)
    .on(transitionMinimumElapsed, () => true)
    .reset(transitionStarted);
const $introSplashSuppressedForLessonId = createStore<number | null>(null).on(
    transitionStarted,
    (_, transition) => transition.nextLesson.lessonId
);

reset({
    clock: [transitionFinished, transitionAborted],
    target: [$lessonTransition, $minDurationElapsed, $introSplashSuppressedForLessonId]
});

const contract = new ModelContract(
    { $lessonTransition, $introSplashSuppressedForLessonId, $transitionEnabled },
    { transitionMinimumElapsed, transitionFinished, transitionAborted },
    {},
    { $progressBeforeSnapshot, transitionStarted }
);

export type ILessonTransitionContract = typeof contract;

/**
 * Drives the overlay shown between a completed lesson and the next one. It starts when the flow hands
 * over to the next lesson, stays up across the navigation, and lets go once the overlay reports the
 * minimum time passed and the new lesson is loaded. The overlay owns the timers (minimum hold and the
 * give-up timeout); the model aborts on any other navigation, a failed completion or logout.
 */
export class LessonTransitionModule extends ModuleDescription<ILessonTransitionContract> {
    constructor(
        public lessonCompletionModule: ILessonCompletionContract,
        public lessonCompletionFlowModule: ILessonCompletionFlowContract,
        public currentLessonModule: ICurrentLessonContract,
        public currentCourseModule: ICurrentCourseContract,
        public lessonsModule: ILessonsContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public userAuthModule: IUserAuthContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: [this.amplitudeFlagsModule.stores.$gamificationEnabled, appMounted],
            source: this.amplitudeFlagsModule.stores.$gamificationEnabled,
            fn: (flag) => flag === true,
            target: $transitionEnabled
        });

        // $currentCourse is re-derived with the new progress before the flow exits: keep the old value.
        sample({
            clock: this.lessonCompletionModule.events.lessonCompletionRequested,
            source: this.currentCourseModule.stores.$currentCourse,
            fn: (course) => clampPercent(Math.floor(course?.progress ?? 0)),
            target: $progressBeforeSnapshot
        });

        sample({
            clock: this.lessonCompletionFlowModule.events.lessonExitStarted,
            source: {
                enabled: $transitionEnabled,
                courseLessons: this.lessonsModule.stores.$courseLessons,
                result: this.lessonCompletionModule.stores.$completionResult,
                before: $progressBeforeSnapshot
            },
            filter: ({ enabled }) => enabled,
            fn: ({ courseLessons, result, before }, exit: LessonExitStart): LessonTransition => {
                const reported = result?.courseProgress;
                const progressTo =
                    typeof reported === "number"
                        ? clampPercent(Math.floor(reported))
                        : (progressAfterCompletion(courseLessons, exit.completedLessonId) ?? before);
                return {
                    phase: "playing",
                    nextLesson: exit.nextLesson,
                    completedLessonId: exit.completedLessonId,
                    isTopicCompleted: exit.isTopicCompleted,
                    nodes: buildMiniMap(courseLessons, exit.completedLessonId, exit.nextLesson.lessonId),
                    progressFrom: before,
                    progressTo: Math.max(before, progressTo),
                    startedAt: Date.now()
                };
            },
            target: transitionStarted
        });

        sample({
            clock: [this.currentLessonModule.stores.$currentLesson.updates, $minDurationElapsed.updates],
            source: {
                transition: $lessonTransition,
                minElapsed: $minDurationElapsed,
                lesson: this.currentLessonModule.stores.$currentLesson
            },
            filter: ({ transition, minElapsed, lesson }) =>
                transition !== null &&
                transition.phase === "playing" &&
                minElapsed &&
                lesson !== null &&
                lesson.id === transition.nextLesson.lessonId,
            fn: () => undefined,
            target: transitionReady
        });

        sample({
            clock: routePathNameChanged,
            source: $lessonTransition,
            filter: (current, pathname) => current !== null && !LESSON_ROUTE.test(pathname),
            fn: () => undefined,
            target: transitionAborted
        });

        sample({
            clock: [
                this.lessonCompletionModule.events.lessonCompletionFailed,
                this.userAuthModule.events.logoutStudent
            ],
            source: $lessonTransition,
            filter: (current) => current !== null,
            fn: () => undefined,
            target: transitionAborted
        });
    }
}
